<template>
  <div class="flex flex-col transition-colors duration-300" :class="[
    isFixedHeight ? 'h-screen' : 'min-h-screen pb-[calc(3.5rem+env(safe-area-inset-bottom))] lg:pb-0',
    'bg-(--nviya-canvas)'
    ]">
    <!-- Impersonation Banner (above everything) -->
    <ImpersonationBanner />

    <!-- Top Navigation Bar — one light bar everywhere (the old blue variant is gone) -->
    <header :class="[
      'fixed left-0 right-0 h-14 z-50 flex items-center px-4 sm:px-6',
      'bg-(--nviya-surface) border-b border-(--nviya-border) bg-[image:var(--nviya-surface-sheen)]',
      isImpersonating ? 'top-10' : 'top-0'
      ]">
      <!-- Logo + wordmark -->
      <NuxtLink to="/" class="flex items-center gap-2.5 mr-2 hover:opacity-80 transition-opacity flex-shrink-0">
        <img
          src="~/assets/images/nviya-logo.png"
          alt="Nviya Logo"
          class="h-8 w-auto"
        />
        <!-- Wordmark only — the tagline is marketing copy and this is chrome.
             The brand IS the way home in a hub-and-spoke app. -->
        <!-- data-studio-skip: our own wordmark is chrome. Studio mode sweeps
             text nodes, and a client called "Nviya ..." would rename the app. -->
        <span data-studio-skip class="hidden sm:block text-[16px] font-bold tracking-[0.02em] text-brand-vivid-azure" style="font-family:'Inter',sans-serif">
          NVIYA PRIME
        </span>
      </NuxtLink>

      <!-- Right-anchored instrument group: scope bar sits flush with the
           utility icons at the same 36px height — identity left, air, then
           everything you operate on the right. -->
      <div class="flex-1"></div>
      <div class="hidden md:flex items-center min-w-0 mr-2">
        <HeaderFilterPills
          :mode="headerFilterMode"
          :show-timeframe="headerShowTimeframe"
          :client-options="clientJumpOptions"
          @portfolio-changed="handlePortfolioChange"
        />
      </div>

      <!-- Right side: Settings + User Avatar -->
      <div class="flex items-center gap-1.5 flex-shrink-0">
        <!-- Outstanding-reviews inbox (approvers only) — /home replaces the
             old judge/review banner with this header popup -->

        <!-- Settings (system-wide: product info + integrations) — matched to
             the avatar: same 36px circle footprint, glyph sized to sit level -->
        <NuxtLink
          v-if="can('client_settings:read')"
          to="/client-settings"
          class="w-9 h-9 flex items-center justify-center rounded-full transition-colors text-slate-400 hover:text-slate-700 hover:bg-(--nviya-figure-bg) dark:text-white/70 dark:hover:text-white dark:hover:bg-white/10"
          title="Settings"
        >
          <FontAwesome :icon="['fas', 'gear']" class="w-[18px] h-[18px]" />
        </NuxtLink>

        <!-- User Avatar with Dropdown — same 36px circle as the cog, same
             muted palette, no border/ring so it doesn't read larger -->
        <div class="relative" ref="userMenuRef">
          <button
            @click="userMenuOpen = !userMenuOpen"
            :class="[
              'w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-colors',
              'bg-(--nviya-figure-bg) text-(--nviya-text-secondary) hover:bg-(--nviya-active) hover:text-(--nviya-text-primary)',
              userMenuOpen ? 'bg-(--nviya-active) text-(--nviya-text-primary)' : ''
              ]"
            title="Account"
          >
            {{ userInitial }}
          </button>

          <!-- User Dropdown Menu -->
          <Transition name="dropdown">
            <div
              v-if="userMenuOpen"
              class="absolute right-0 mt-2 w-56 rounded-xl shadow-(--nviya-shadow-lg) n-panel border py-2 z-50"
            >
              <div class="px-4 py-3 border-b border-(--nviya-border)">
                <p class="text-sm font-semibold n-heading">{{ user?.username || 'User' }}</p>
                <p class="text-xs text-slate-500 truncate">{{ user?.email || '' }}</p>
              </div>
              <!-- Admin console entry/exit lives here (standard avatar-menu
                   pattern) — the header chip next to the logo is the way back -->
              <template v-if="isAdminUser">
                <!-- One link, always: you can either see the admin console or
                     you can't. No mode, no exit — leaving is just navigating. -->
                <NuxtLink
                  to="/dashboard/admin"
                  @click="userMenuOpen = false"
                  class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 hover:bg-(--nviya-figure-bg) transition-colors"
                >
                  <FontAwesome :icon="['fas', 'shield-halved']" class="w-4 h-4" />
                  Admin console
                </NuxtLink>
                <!-- ⚠ TWO DOORS SINCE 26 Oct 2026, and this is the deliberate
                     one of the pair. The console was first rejected as already
                     overloaded; it is now ALSO listed there under Reports &
                     Alerts beside Doozy Watch, because that is where someone
                     goes when they are asking "is our output any good" rather
                     than glancing at their own portfolio. Keep both: this one
                     reads "{portfolio} health" and is portfolio scoped like
                     everything else in this menu. Still not in the top bar —
                     judge verdicts are a view of our OWN output, so no RM
                     surface carries them. -->
                <NuxtLink
                  to="/judge-watch"
                  @click="userMenuOpen = false"
                  class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 hover:bg-(--nviya-figure-bg) transition-colors"
                >
                  <FontAwesome :icon="['fas', 'scale-balanced']" class="w-4 h-4" />
                  {{ portfolioStore.activePortfolioName ? `${portfolioStore.activePortfolioName} health` : 'Health' }}
                </NuxtLink>
                <div class="mx-3 my-1 border-t border-(--nviya-border)"></div>
              </template>
              <NuxtLink
                to="/account/notifications"
                @click="userMenuOpen = false"
                class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 hover:bg-(--nviya-figure-bg) transition-colors"
              >
                <FontAwesome :icon="['fas', 'envelope']" class="w-4 h-4" />
                Email preferences
              </NuxtLink>
              <!-- Connecting an AI client is per-user (own login, own
                   portfolio scope), so it belongs here rather than on the
                   system-wide client settings page -->
              <NuxtLink
                to="/account/connections"
                @click="userMenuOpen = false"
                class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 hover:bg-(--nviya-figure-bg) transition-colors"
              >
                <FontAwesome :icon="['fas', 'robot']" class="w-4 h-4" />
                AI connections
              </NuxtLink>
              <!-- Appearance is personal, so it lives here — menu stays open
                   so the switch is visible immediately -->
              <button
                @click="toggleTheme"
                class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 hover:bg-(--nviya-figure-bg) transition-colors"
              >
                <FontAwesome :icon="['fas', isDark ? 'sun' : 'moon']" class="w-4 h-4" />
                {{ isDark ? 'Light mode' : 'Dark mode' }}
              </button>
              <!-- Studio mode — invented client names, for filming and demos.
                   Deliberately quiet: no badge, no banner, no tint anywhere on
                   the page, because the page is what ends up in the footage.
                   The dot here is the only tell, and this menu is never in
                   frame when it matters. -->
              <button
                @click="toggleStudio"
                class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 hover:bg-(--nviya-figure-bg) transition-colors"
              >
                <FontAwesome :icon="['fas', 'eye-slash']" class="w-4 h-4" />
                Studio mode
                <span v-if="isStudio" class="ml-auto w-1.5 h-1.5 rounded-full bg-(--nviya-accent)"></span>
              </button>
              <button
                @click="handleLogout"
                class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 hover:bg-(--nviya-figure-bg) transition-colors"
              >
                <FontAwesome :icon="['fas', 'right-from-bracket']" class="w-4 h-4" />
                Sign out
              </button>
            </div>
          </Transition>
        </div>

        <!-- Mobile menu button -->
        <button
          @click="mobileSidebarOpen = true"
          class="lg:hidden w-9 h-9 flex items-center justify-center rounded-full transition-colors ml-2 text-slate-400 hover:text-slate-700 hover:bg-(--nviya-figure-bg) dark:text-white/70 dark:hover:text-white dark:hover:bg-white/10"
        >
          <FontAwesome :icon="['fas', 'bars']" class="w-5 h-5" />
        </button>
      </div>
    </header>

    <!-- Mobile Sidebar Overlay -->
    <Transition name="fade">
      <div
        v-if="mobileSidebarOpen"
        class="fixed inset-0 bg-black/50 z-40 lg:hidden"
        @click="mobileSidebarOpen = false"
      ></div>
    </Transition>

    <!-- Left Sidebar — MOBILE menu only. The desktop admin rail is
         AdminRail, in the content row below; this drawer is the small-screen
         equivalent and renders the same tree flat. -->
    <aside :class="[
      'fixed left-0 z-50 border-r flex flex-col transition-all duration-300 ease-in-out w-60 lg:hidden',
      isImpersonating ? 'top-24 h-[calc(100vh-6rem)]' : 'top-14 h-[calc(100vh-3.5rem)]',
      mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full',
      'bg-(--nviya-surface) border-(--nviya-border) shadow-(--nviya-shadow-lg)'
      ]">
      <!-- Sidebar Header: Controls -->
      <div :class="['px-2 py-2 border-b', 'border-(--nviya-border)']">
        <!-- Mobile sidebar: Portfolio selector + close -->
        <div v-if="mobileSidebarOpen" class="flex items-center justify-between mb-1">
          <div class="flex-1 min-w-0">
            <PortfolioSelector
              :collapsed="false"
              @portfolio-changed="handlePortfolioChange"
            />
          </div>
          <button
            @click="mobileSidebarOpen = false"
            :class="['p-1.5 rounded-(--nviya-radius-md) n-row text-(--nviya-text-muted)']"
          >
            <FontAwesome :icon="['fas', 'times']" class="w-3 h-3" />
          </button>
        </div>
        <!-- Mobile sidebar: Admin console link (admins only) -->
        <NuxtLink
          v-if="isAdminUser && mobileSidebarOpen"
          to="/dashboard/admin"
          @click="mobileSidebarOpen = false"
          class="mt-2 flex items-center gap-3 px-3 py-2 rounded-(--nviya-radius-md) text-sm font-medium n-row text-(--nviya-text-secondary) hover:text-(--nviya-text-primary)"
        >
          <FontAwesome :icon="['fas', 'shield-halved']" class="w-4 h-4" />
          Admin console
        </NuxtLink>
      </div>

      <!-- Sidebar Navigation -->
      <nav
        ref="sidebarNavRef"
        class="flex-1 py-2 overflow-y-auto sidebar-scroll"
        :style="{
          visibility: sidebarNavReady ? 'visible' : 'hidden',
          opacity: sidebarNavReady ? 1 : 0
        }"
      >
        <!-- Mobile: Show top nav items in sidebar -->
        <div class="lg:hidden mb-2">
          <NuxtLink
            v-for="item in visibleTopNavItems"
            :key="item.id"
            :to="item.route"
            :class="[
              'flex items-center gap-3 mx-2 px-3 py-2 rounded-(--nviya-radius-md) text-sm font-medium transition-all',
              isActiveRoute(item.route)
              ? 'bg-(--nviya-selected-bg) text-(--nviya-accent-on-soft) font-semibold'
              : 'n-row text-(--nviya-text-secondary) hover:text-(--nviya-text-primary)'
              ]"
            @click="mobileSidebarOpen = false"
          >
            <FontAwesome :icon="['fas', getTopNavIcon(item.id)]" class="w-4 h-4 flex-shrink-0" />
            <span>{{ item.label }}</span>
          </NuxtLink>
          <div :class="['my-2 mx-3 border-t', 'border-(--nviya-border)']"></div>
        </div>

        <template v-for="item in sidebarItems" :key="item.id || item.label">
          <!-- Section Divider -->
          <div
            v-if="item.type === 'section'"
            v-show="isItemVisible(item)"
            class="mt-4 mb-1 px-3"
          >
            <span :class="['typo-eyebrow', 'text-(--nviya-text-tertiary)']">
              {{ item.label }}
            </span>
          </div>

          <!-- Navigation Item -->
          <NuxtLink
            v-else
            v-show="isItemVisible(item)"
            :to="item.route"
            :class="[
              'flex items-center gap-3 mx-2 px-3 py-2 rounded-(--nviya-radius-md) text-sm font-medium transition-all',
              isActiveRoute(item.route)
              ? 'bg-(--nviya-selected-bg) text-(--nviya-accent-on-soft) font-semibold'
              : 'n-row text-(--nviya-text-secondary) hover:text-(--nviya-text-primary)'
              ]"
            @click="saveSidebarScroll(); mobileSidebarOpen = false"
          >
            <FontAwesome :icon="['fas', item.icon]" class="w-4 h-4 flex-shrink-0" />
            <span>{{ item.label }}</span>
          </NuxtLink>
        </template>
      </nav>

    </aside>

    <!-- Main Content Wrapper -->
    <div :class="['flex-1 flex flex-col transition-all duration-300', isImpersonating ? 'pt-24' : 'pt-14']">
      <!-- Main Content Area -->
      <!-- CLIP, not hidden, on the x axis of a page that scrolls normally.
           `overflow-x: hidden` with `overflow-y: visible` is not a legal pair:
           CSS computes the visible axis to `auto`, so both of these wrappers
           silently became vertical scroll CONTAINERS. On a `min-h-screen`
           page the body is already scrolling the same content, so the window
           showed two scrollbars 15px apart down the right-hand edge — the
           page's, and the pane's showing the same overflow again.
           `clip` is the axis-independent one: the spec exempts it, so
           `overflow-y` stays `visible` and only the body scrolls. Fixed-height
           pages are unchanged — they WANT an inner scroller, and keep
           `overflow-hidden` / `overflow-x-hidden` to get it. -->
      <div class="flex flex-1 transition-colors duration-300" :class="[isFixedHeight ? 'overflow-hidden' : 'overflow-x-clip', 'bg-(--nviya-canvas)']">
        <!-- Desktop admin rail. Shown on exactly the pages that get admin
             breadcrumbs (isAdminView), so the chrome and the trail always
             agree about whether you are in the admin area. Mobile keeps the
             slide-in drawer above. -->
        <AdminRail
          v-if="isAdminView"
          :top-offset="isImpersonating ? '6rem' : '3.5rem'"
        />
        <!-- Fixed-height pages lose the bottom tab bar's height on mobile so
             composers/toolbars pinned to the page bottom stay reachable. -->
        <div
          class="flex-1 flex flex-col transition-colors duration-300"
          :class="[
            'bg-(--nviya-canvas)',
            isFixedHeight
              ? 'h-[calc(100dvh-7rem-env(safe-area-inset-bottom))] lg:h-[calc(100vh-3.5rem)] overflow-x-hidden'
              : 'overflow-x-clip'
            ]"
        >
          <!-- Breadcrumbs (hidden when there's only a single, non-navigational crumb, e.g. dashboard) -->
          <nav
            v-if="breadcrumbs && breadcrumbs.length > 1"
            aria-label="Breadcrumb"
            :class="[
              'flex-shrink-0 border-b text-xs',
              'bg-(--nviya-surface-alt) border-(--nviya-border) text-(--nviya-text-muted)'
              ]"
          >
            <!-- Light-header pages are full-width, so their crumbs hug the
                 same left edge as the filter bar instead of centring at 7xl -->
            <div :class="'w-full px-4 sm:px-6 py-1 flex items-center flex-wrap gap-1.5'">
              <template v-for="(crumb, i) in breadcrumbs" :key="i">
                <FontAwesome
                  v-if="i > 0"
                  :icon="['fas', 'chevron-right']"
                  class="w-2.5 h-2.5 opacity-50 flex-shrink-0"
                />
                <NuxtLink
                  v-if="crumb.to"
                  :to="crumb.to"
                  :class="[
                    'flex items-center gap-1 transition-colors hover:underline',
                    'hover:text-(--nviya-text-primary)'
                    ]"
                >
                  <FontAwesome v-if="crumb.icon" :icon="['fas', crumb.icon]" class="w-3 h-3" />
                  <span>{{ crumb.label }}</span>
                </NuxtLink>
                <span
                  v-else
                  :class="['flex items-center gap-1 font-semibold truncate max-w-[16rem]', 'text-(--nviya-text-primary)']"
                >
                  <FontAwesome v-if="crumb.icon" :icon="['fas', crumb.icon]" class="w-3 h-3 flex-shrink-0" />
                  <span class="truncate">{{ crumb.label }}</span>
                </span>
              </template>
            </div>
          </nav>

          <!-- Recovery-grade session banner (signed in with a one-time code) -->
          <div
            v-if="otpLogin"
            :class="[
              'flex-shrink-0 border-b px-4 sm:px-6 py-2.5',
              isDark ? 'bg-(--nviya-gold)/10 border-(--nviya-gold)/30' : 'bg-amber-50 border-amber-200'
              ]"
          >
            <div class="w-full flex flex-wrap items-center gap-x-4 gap-y-2">
              <div class="flex items-center gap-2 min-w-0">
                <FontAwesome :icon="['fas', 'shield-halved']" :class="['w-3.5 h-3.5 flex-shrink-0', 'text-(--nviya-warning-text)']" />
                <p :class="['text-sm min-w-0', isDark ? 'text-white' : 'text-amber-900']">
                  <span class="font-semibold">Signed in with a one-time code.</span>
                  <span :class="'text-(--nviya-warning-text)'"> Sensitive actions may ask for a second step. Set a new password? Optional — skip and your old one still works.</span>
                </p>
              </div>
              <div class="flex items-center gap-2 ml-auto flex-shrink-0">
                <button
                  v-if="otpResetToken"
                  type="button"
                  :class="[
                    'text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors cursor-pointer',
                    isDark ? 'bg-(--nviya-gold) text-(--nviya-text-primary) hover:bg-(--nviya-gold)' : 'bg-amber-600 text-white hover:bg-amber-700'
                    ]"
                  @click="goSetNewPassword"
                >
                  Set a new password
                </button>
                <button
                  type="button"
                  :class="[
                    'text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors cursor-pointer',
                    isDark ? 'border-(--nviya-gold)/40 text-(--nviya-gold) hover:bg-(--nviya-gold)/10' : 'border-amber-300 text-amber-800 hover:bg-amber-100'
                    ]"
                  @click="dismissOtpBanner"
                >
                  Later
                </button>
              </div>
            </div>
          </div>

          <!-- Fixed-height pages (chat/reports) fill the space left below the breadcrumb bar -->
          <div v-if="isFixedHeight" class="flex-1 min-h-0 flex flex-col">
            <slot />
          </div>
          <slot v-else />
        </div>
      </div>

    </div>

    <!-- Site Footer (full width, below sidebar) -->
    <footer v-if="!isFixedHeight" :class="['border-t py-4 px-6 relative z-50', 'bg-(--nviya-surface) border-(--nviya-border)']">
      <div class="w-full flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs" :class="['text-(--nviya-text-tertiary)']">
        <span data-studio-skip>&copy; {{ new Date().getFullYear() }} <a href="https://www.nviya-prime.com" target="_blank" rel="noopener" class="hover:underline">Nviya Prime</a></span>
        <span>&middot;</span>
        <a href="https://www.nviya-prime.com" target="_blank" rel="noopener" class="hover:underline">www.nviya-prime.com</a>
        <span>&middot;</span>
        <a href="https://www.nviya-prime.com/contact" target="_blank" rel="noopener" class="hover:underline">Contact</a>
        <span>&middot;</span>
        <span>BE: Version {{ backendVersion || '—' }}</span>
        <span>&middot;</span>
        <span>FE: Version {{ frontendVersion }}</span>
      </div>
    </footer>

    <!-- Notification Panel -->
    <NotificationPanel
      :isOpen="showNotificationPanel"
      :notifications="notifications"
      @close="closeNotifications"
      @markAsRead="markAsRead"
      @markAllAsRead="markAllAsRead"
      @clearAll="clearAll"
      @navigate="handleNavigate"
    />

    <!-- Notification Toasts (below top nav) -->
    <div :class="['fixed right-4 z-50 space-y-2', isImpersonating ? 'top-[7rem]' : 'top-[4.5rem]']">
      <NotificationToast
        v-for="toast in activeToasts"
        :key="toast.id"
        :notification="toast"
        :show="true"
        @close="removeToast(toast.id)"
        @navigate="handleNavigate"
        @action="handleToastAction"
      />
    </div>

    <!-- Client Pack build: filmstrip panel + minimised pill -->
    <ClientPackProgress />

    <!-- A running onboarding, from anywhere in the app -->
    <OnboardingProgressPill />

    <!-- General site feedback widget (hidden on fixed-height chat pages to avoid overlapping the composer) -->
    <SiteFeedbackWidget v-if="!isFixedHeight" />

    <!-- Persistent bottom tab bar (mobile only) — Home / Companies / Alerts /
         Chat one tap away; Menu opens the existing drawer -->
    <MobileBottomNav @open-menu="mobileSidebarOpen = true" />
  </div>
</template>

<script setup>
import MobileBottomNav from '~/components/MobileBottomNav.vue'
import ClientPackProgress from '@/components/pitch-pack/ClientPackProgress.vue'
import OnboardingProgressPill from '@/components/onboarding/OnboardingProgressPill.vue'
import { useClientPackJob } from '@/composables/useClientPackJob'
import companyService from '@/api/services/company'
import HeaderFilterPills from '@/components/dashboard/HeaderFilterPills.vue'
import { usePermissions } from '@/composables/usePermissions'
import { usePortfolioStore } from '@/store/usePortfolioStore'
import { useImpersonation } from '@/composables/useImpersonation'
import { useStudioMode } from '@/composables/useStudioMode'
import { adminSidebarItems } from '@/config/adminNav'
import AdminRail from '@/components/admin/AdminRail.vue'

const { logout, user } = useAuth()
const { isImpersonating, checkImpersonationStatus } = useImpersonation()
const { can, canAny } = usePermissions()
const route = useRoute()
const router = useRouter()

// Portfolio store
const portfolioStore = usePortfolioStore()

// Studio mode — invented client names for filming and demos.
//
// Seeded from the FULL managed-company list, not from whatever is on screen.
// A company that is only ever named inside an impact report's prose has no
// company_name field to register it, so without the seed it would survive the
// sweep and put a real client in the footage.
const {
  isStudio, enter: enterStudio, leave: leaveStudio,
  seed: seedStudio, start: startStudio,
} = useStudioMode()

const studioSeeds = async () => {
  let companies = []
  try {
    const result = await companyService.getAllManagedCompanies()
    companies = (result.companies || []).map((c) => c.displayName).filter(Boolean)
  } catch (e) {
    // Degrade to on-screen names rather than refusing to enter the mode.
    console.warn('Studio mode: company list unavailable, seeding from screen only', e)
  }
  return {
    companies,
    portfolios: portfolioStore.portfolios.map((p) => p.name).filter(Boolean),
  }
}

const toggleStudio = async () => {
  userMenuOpen.value = false
  // Leaving reloads: restoring the original text would mean shadowing every
  // node we rewrote, and a reload is exact.
  if (isStudio.value) return leaveStudio()
  enterStudio(await studioSeeds())
}

// Sidebar is the MOBILE menu only — always expanded when open.
const mobileSidebarOpen = ref(false)

// Recovery-grade session: set by useAuth.signinWithCode after a one-time
// code login. Shows a banner + optional set-a-new-password nudge until
// dismissed for the session or the user signs out.
const otpLogin = ref(typeof window !== 'undefined' && sessionStorage.getItem('otpLogin') === '1')
const otpResetToken = ref(typeof window !== 'undefined' ? sessionStorage.getItem('otpResetToken') || '' : '')

const dismissOtpBanner = () => {
  otpLogin.value = false
  sessionStorage.removeItem('otpLogin')
}

const goSetNewPassword = () => {
  const token = otpResetToken.value
  dismissOtpBanner()
  sessionStorage.removeItem('otpResetToken')
  navigateTo({ path: '/auth/password-reset', query: { token } })
}

// There is no admin "mode" — you either hold admin:read or you don't, and
// you're in the admin area iff the URL is an admin route. No stored state.
const isAdminUser = computed(() => can('admin:read'))
const isAdminView = computed(() =>
  isAdminUser.value && (route.path.startsWith('/admin') || route.path === '/dashboard/admin'))

// UI state
const showNotificationPanel = ref(false)
const activeToasts = ref([])
const toastQueue = ref([])
const isShowingToast = ref(false)
const sidebarNavRef = ref(null)
const userMenuRef = ref(null)
const userMenuOpen = ref(false)
// Persist scroll position across layout remounts
const sidebarScrollPosition = useState('sidebar-scroll', () => 0)
// Hide sidebar nav during scroll positioning to prevent visible jump
const sidebarNavReady = ref(false)

// Top navigation items (main nav bar)
// `anyPermissions`: hide the item when the user has none of the listed perms.
const topNavItems = [
  { id: 'dashboard', label: 'Dashboard', route: '/dashboard' },
  { id: 'companies', label: 'Companies', route: '/companies' },
  { id: 'alerts', label: 'Alerts', route: '/alerts' },
  // Sector Insights (spec 005 Q14): the /sector-insights list sits beside Alerts.
  { id: 'sector-insights', label: 'Sectors', route: '/sector-insights', anyPermissions: ['sector_insights:read'] },
]

const visibleTopNavItems = computed(() =>
  topNavItems
    .filter((item) => !item.anyPermissions || canAny(...item.anyPermissions))
    .map((item) => {
      if (item.id === 'alerts' && route.params.name) {
        return { ...item, route: `/alerts?company=${encodeURIComponent(route.params.name)}` }
      }
      return item
    })
)

// Icon mapping for top nav items (used in mobile sidebar)
const getTopNavIcon = (id) => {
  const icons = {
    dashboard: 'gauge-high',
    companies: 'building',
    events: 'calendar-check',
    alerts: 'bell',
    'sector-insights': 'industry',
    'pitch-deck': 'file-powerpoint',
  }
  return icons[id] || 'circle'
}

// ── Header filter bar ──────────────────────────────────────────────
// One HeaderFilterPills renders on every page; each page's route meta says
// how its search box behaves and whether the time-frame pill shows.
//   headerFilter: 'list'  → search filters the page's list (shared store)
//   headerFilter: 'jump'  → search navigates to a client (default)
//   headerTimeframe: true → show the time-frame pill
const headerFilterMode = computed(() => route.meta.headerFilter || 'jump')
const headerShowTimeframe = computed(() => !!route.meta.headerTimeframe)

// Company names for the search datalist (shared by both modes).
const clientJumpOptions = ref([])
const loadClientJumpOptions = async () => {
  if (typeof window === 'undefined') return
  if (clientJumpOptions.value.length) return
  try {
    const response = await companyService.getCompanyList()
    clientJumpOptions.value = (response.companies || []).filter((c) => c !== 'Any')
  } catch { /* silent — the pill just keeps an empty datalist */ }
}
loadClientJumpOptions()

// Sidebar items - secondary navigation. Derived from the ONE admin tree in
// config/adminNav.js, as are the breadcrumbs below and the landing page's
// boxes, so the three cannot drift apart.
const sidebarItems = adminSidebarItems

// Computed set of visible item keys (reactive, prevents DOM reflows)
// Filters by both permission and current view mode
const visibleItemKeys = computed(() => {
  const visible = new Set()
  const currentView = isAdminView.value ? 'admin' : 'business'
  for (const item of sidebarItems) {
    const key = item.id || item.label
    const permissionOk = !item.permission || can(item.permission)
    const viewOk = !item.view || item.view === currentView
    if (permissionOk && viewOk) {
      visible.add(key)
    }
  }
  return visible
})

// Check if an item should be visible
const isItemVisible = (item) => {
  return visibleItemKeys.value.has(item.id || item.label)
}

// --- System Health (heartbeat icon) ---
// Module-level singleton so data persists across layout re-mounts (page navigation)
if (!globalThis.__systemPipelines) {
  globalThis.__systemPipelines = ref({})
  globalThis.__systemHealthInterval = null
}
const systemPipelines = globalThis.__systemPipelines

const systemHealthColor = computed(() => {
  const statuses = Object.values(systemPipelines.value).filter(Boolean).map(p => p.status)
  if (!statuses.length) return 'text-white/40' // no data yet
  if (statuses.some(s => s === 'failed')) return 'text-red-400'
  if (statuses.some(s => s === 'running')) return 'text-info animate-pulse'
  if (statuses.every(s => s === 'completed')) return 'text-green-400'
  return 'text-amber-400' // mixed / partial
})

const systemHealthLabel = computed(() => {
  const statuses = Object.values(systemPipelines.value).filter(Boolean).map(p => p.status)
  if (!statuses.length) return 'Unknown'
  if (statuses.some(s => s === 'failed')) return 'Issues detected'
  if (statuses.some(s => s === 'running')) return 'Running'
  if (statuses.every(s => s === 'completed')) return 'All systems healthy'
  return 'Partial'
})

const fetchSystemHealth = async () => {
  try {
    const pipelineService = (await import('~/api/services/pipeline')).default
    const result = await pipelineService.getPipelinesStatus()
    systemPipelines.value = result.pipelines
  } catch { /* silent */ }
}

// Pages carrying the /home-style light header (white bar + wordmark, no
// central blue nav) — they own their filters via the sticky pill bar.
// '/' redirects to /home; treating it as light avoids a dark-header flash.
const LIGHT_HEADER_BASES = ['/home', '/company-hub',
  '/opportunities', '/pitches', '/meetings']
const isHomePage = computed(() => {
  // Normalise: '/home/' and child routes ('/opportunities/123') must match
  // too — an exact Set lookup let those fall back to the old blue header.
  const p = (route.path || '/').replace(/\/+$/, '') || '/'
  if (p === '/') return true
  return LIGHT_HEADER_BASES.some((b) => p === b || p.startsWith(b + '/'))
})

// Check if route is active
const isActiveRoute = (path) => {
  if (path === '/dashboard') {
    return route.path === '/dashboard'
  }
  if (path === '/chat') {
    return route.path.startsWith('/chat')
  }
  if (path === '/companies') {
    return route.path.startsWith('/companies/') || route.path === '/companies' || route.path.startsWith('/company-intelligence/')
  }
  // Exact match or match with trailing slash (for nested routes)
  // This prevents /admin/prompts from matching /admin/prompts-export
  return route.path === path || route.path.startsWith(path + '/')
}

// Friendly labels for top-level user/business-view route segments
const SEGMENT_LABELS = {
  dashboard: 'Dashboard',
  companies: 'Companies',
  'company-hub': 'Client Research Hub',
  'company-intelligence': 'Companies',
  reports: 'Reports',
  'v5-reports': 'Reports',
  chat: 'Chat',
  onboarding: 'Add Company',
  news: 'Global News',
  radar: 'Radar',
  timeline: 'Timeline',
  settings: 'Settings',
  help: 'Help',
  editorial: 'Editorial',
  'label-mapping': 'Rename Risk Types (v2)',
  models: 'Forecasting',
  alerts: 'Alerts',
  'alert-view-editor': 'Alert Editor',
  'pitch-deck': 'Pitch Deck',
  // Impact report detail routes — without an entry here the breadcrumb falls
  // back to titleizeSegment and renders the raw slug (e.g. "Impact V5"), which
  // reads as a leaked internal path. These have no index page, so their section
  // crumb is rendered non-clickable (see NO_INDEX_SECTIONS below).
  'impact-v5': 'Impact Report',
  'impact-v4': 'Impact Report',
  'impact-v2': 'Impact Report',
  'impact-v1': 'Impact Report',
  'operations-supply-chain': 'Operations & Supply Chain',
  labs: 'Labs',
  // Without this the trail titleizes the slug to "Sector Insights", which is
  // the retired name of the surface and the one thing this page is not. The
  // nav entry one file up reads "Sector research" for the same reason.
  'sector-insights': 'Sector research',
}

// Turn an unknown slug into a readable label ("foo-bar" -> "Foo Bar")
const titleizeSegment = (seg) =>
  decodeURIComponent(seg).replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

// Breadcrumbs shown above page content. Admin view derives crumbs from
// sidebarItems (single source of truth); user view derives them from the path.
const breadcrumbs = computed(() => {
  const path = route.path.replace(/\/+$/, '') || '/'

  // Auth pages render outside this layout, but guard just in case
  if (path.startsWith('/auth') || path === '/reset-password') return null

  // --- Admin view ---
  // An admin nav entry does not have to live under /admin: Resolution Judge
  // sits in the Agents section but points at /track-record, whose read surface
  // is deliberately open to any logged-in user. Matching only on the path
  // prefix dropped it out of the admin branch entirely, so an agents page
  // reached from Admin → Agents → Resolution Judge showed "Home > Track
  // Record" and lost its section. Match the nav instead — but only give admin
  // crumbs while in admin view, so the same page still reads as a user page
  // for the people it was opened up to.
  let navBest = null
  {
    let currentSection = null
    for (const item of sidebarItems) {
      if (item.type === 'section') { currentSection = item.label; continue }
      if (!item.route) continue
      if (path === item.route || path.startsWith(item.route + '/')) {
        if (!navBest || item.route.length > navBest.route.length) {
          navBest = { route: item.route, label: item.label, section: currentSection }
        }
      }
    }
  }

  // The admin landing page keeps its single, non-navigational crumb — it is
  // the breadcrumb's own root, so "Admin > Overview" would point at itself.
  if (path !== '/dashboard/admin'
      && (path.startsWith('/admin') || (isAdminView.value && navBest))) {
    const best = navBest

    const crumbs = [{ label: 'Admin', to: '/dashboard/admin', icon: 'home' }]
    if (!best) return crumbs
    // A nav section ("Data", "Agents") groups pages; it has no page of its
    // own. It stays plain text — linking it at the section's first page sent
    // "Data" to Manage Companies, which reads as a broken crumb.
    if (best.section) crumbs.push({ label: best.section })

    const rest = path.slice(best.route.length).split('/').filter(Boolean)
    if (rest.length) {
      crumbs.push({ label: best.label, to: best.route })
      crumbs.push({ label: decodeURIComponent(rest[rest.length - 1]) })
    } else {
      crumbs.push({ label: best.label })
    }
    return crumbs
  }

  // --- User / business view ---
  const segs = path.split('/').filter(Boolean)
  // Admin dashboard lives under /dashboard/admin — treat it as the admin home
  if (path === '/dashboard/admin') return [{ label: 'Admin', icon: 'home' }]
  // Root and /home are the landing page — single crumb hides the breadcrumb bar
  if (segs.length === 0 || path === '/home') return [{ label: 'Home', icon: 'home' }]
  // The classic dashboard is now a page of its own under Home
  if (path === '/dashboard') return [{ label: 'Home', to: '/home', icon: 'home' }, { label: 'Dashboard' }]

  // Alerts page — append a "Favourites" crumb when the favourites filter is
  // on (arrived via /alerts?favourites=1).
  if (path === '/alerts') {
    const fav = ['1', 'true'].includes(String(route.query.favourites))
    const crumbs = [{ label: 'Home', to: '/home', icon: 'home' }]
    crumbs.push(fav ? { label: 'Alerts', to: '/alerts' } : { label: 'Alerts' })
    if (fav) crumbs.push({ label: 'Favourites' })
    return crumbs
  }

  const crumbs = [{ label: 'Home', to: '/home', icon: 'home' }]
  const first = segs[0]
  const firstLabel = SEGMENT_LABELS[first] || titleizeSegment(first)
  // Routes whose detail segment is an opaque id — show the section, not the id
  const hideDetail = new Set(['chat'])
  // First-segments with no bare index page — section crumb is plain text, not a
  // link (e.g. /impact-v5 only has [run_id], so linking there would 404)
  const NO_INDEX_SECTIONS = new Set(['impact-v5', 'impact-v4', 'impact-v2', 'impact-v1'])
  if (segs.length === 1 || hideDetail.has(first)) {
    crumbs.push({ label: firstLabel })
  } else {
    crumbs.push(NO_INDEX_SECTIONS.has(first) ? { label: firstLabel } : { label: firstLabel, to: '/' + first })
    crumbs.push({ label: titleizeSegment(segs[segs.length - 1]) })
  }
  return crumbs
})

// User initial for avatar
const userInitial = computed(() => {
  const name = user.value?.username || user.value?.email || ''
  return name.charAt(0).toUpperCase() || '?'
})

// Dark mode state
const { isDark, initialize: initializeDarkMode, toggleTheme } = useDarkMode()

// Use fixed height layout for chat pages
const slots = useSlots()
const isFixedHeight = computed(() => !!slots.title)

// Notification system
const {
  notifications,
  hasUnread,
  unreadCount,
  addNotification,
  removeNotification,
  markAsRead,
  markAllAsRead,
  clearAll
} = useNotifications()

import { useLabelMappingStore } from '@/store/useLabelMappingStore'
import healthService from '@/api/services/health'
const config = useRuntimeConfig()
const labelMappingStore = useLabelMappingStore()

// App versions shown in the site footer. FE is baked in at build time; BE is
// fetched from the API health endpoint on mount.
const frontendVersion = config.public.appVersion
const backendVersion = ref(null)

// Track if layout has been initialized (prevents duplicate init on remount)
const layoutInitialized = useState('layout-initialized', () => false)

// Close user menu on click outside
const handleUserMenuClickOutside = (event) => {
  if (userMenuRef.value && !userMenuRef.value.contains(event.target)) {
    userMenuOpen.value = false
  }
}

// Initialize
onMounted(async () => {
  initializeDarkMode()

  // Check impersonation status on layout load
  checkImpersonationStatus()

  // Fetch backend version for the footer (non-blocking)
  healthService.getHealth()
    .then((health) => { backendVersion.value = health.version })
    .catch(() => { backendVersion.value = null })

  // Add click outside listener for user menu
  document.addEventListener('click', handleUserMenuClickOutside)

  // Studio mode persists across reloads — re-seed the map and re-attach the
  // sweep before the first screen settles.
  if (isStudio.value) {
    studioSeeds().then((seeds) => { seedStudio(seeds); startStudio() })
  }

  // Restore exact scroll position, then reveal nav
  if (sidebarNavRef.value) {
    if (sidebarScrollPosition.value > 0) {
      sidebarNavRef.value.scrollTop = sidebarScrollPosition.value
    }
    // Reveal nav after scroll is set
    sidebarNavReady.value = true
  }

  // Only run initialization once per session
  if (!layoutInitialized.value) {
    layoutInitialized.value = true

    // Set admin status for portfolio store
    portfolioStore.setAdminStatus(can('admin:read'))

    // Initialize portfolio store
    try {
      await portfolioStore.initialize()
    } catch (e) {
      console.error('Failed to initialize portfolio store:', e)
    }

    // Start system health polling (heartbeat icon) — skip if already polling
    if (can('admin:read') && !globalThis.__systemHealthInterval) {
      fetchSystemHealth()
      globalThis.__systemHealthInterval = setInterval(fetchSystemHealth, 60000)
    }

    // Load label mappings
    try {
      await labelMappingStore.loadMappings()
    } catch (e) {
      console.error('Failed to preload label mappings:', e)
    }

    // Listen for RBAC 403 Forbidden events
    window.addEventListener('rbac:forbidden', handleRbacForbidden)
  }
})

// Clean up
onUnmounted(() => {
  // Don't clear systemHealthInterval — it's a global singleton that persists across re-mounts
  window.removeEventListener('rbac:forbidden', handleRbacForbidden)
  document.removeEventListener('click', handleUserMenuClickOutside)
})


// Save sidebar scroll position before navigation
const saveSidebarScroll = () => {
  if (sidebarNavRef.value) {
    sidebarScrollPosition.value = sidebarNavRef.value.scrollTop
  }
}

// Handle route changes - scroll restoration happens in onMounted when component remounts
watch(() => route.path, () => {
  // Close mobile sidebar
  mobileSidebarOpen.value = false
})


// Notification functions
const toggleNotifications = () => {
  showNotificationPanel.value = !showNotificationPanel.value
}

const closeNotifications = () => {
  showNotificationPanel.value = false
}

// Logout handler
const handleLogout = async () => {
  // Clear portfolio state
  portfolioStore.clearState()
  await logout()
  navigateTo('/auth/signin')
}

// Portfolio change handler - refresh data when switching portfolios
const handlePortfolioChange = async (portfolio) => {
  // Emit event for any listening pages (before refresh)
  window.dispatchEvent(new CustomEvent('portfolio:changed', {
    detail: { portfolio }
  }))

  // Force a full page refresh to ensure all data is reloaded with new portfolio context
  // This is the most reliable way to ensure all pages respect the portfolio filter
  reloadNuxtApp({ ttl: 1000 })
}

const handleNavigate = async (url) => {
  navigateTo(url)
}

// RBAC 403 handler
const handleRbacForbidden = (event) => {
  addNotification(
    'Permission Denied',
    event.detail?.message || 'You do not have permission to perform this action',
    null,
    'error'
  )
}

// Toast queue management
const showNextToast = () => {
  if (toastQueue.value.length > 0 && !isShowingToast.value) {
    const nextToast = toastQueue.value.shift()
    activeToasts.value = [nextToast]
    isShowingToast.value = true
  }
}

const removeToast = (id) => {
  const index = activeToasts.value.findIndex(toast => toast.id === id)
  if (index > -1) {
    activeToasts.value.splice(index, 1)
    isShowingToast.value = false
    setTimeout(() => showNextToast(), 100)
  }
}

const addToastToQueue = (notification) => {
  toastQueue.value.push(notification)
  showNextToast()
}

// Toast each new notification once. This used to compare the new and old
// array lengths in a deep watcher, but addNotification unshifts in place, and
// Vue hands a deep watcher the SAME array as both values on an in-place
// mutation — so the lengths were always equal and no toast ever showed.
// The seen-ids list lives in useState, not in this component: the layout
// remounts across pages, and a local Set would re-toast every unread
// notification on each remount.
const TOASTED_TYPES = new Set(['impacts', 'client_pack'])
const toastedIds = useState('toasted-notification-ids', () => [])
watch(() => notifications.value.map(n => n.id), (ids) => {
  for (const id of ids) {
    if (toastedIds.value.includes(id)) continue
    toastedIds.value.push(id)
    const notification = notifications.value.find(n => n.id === id)
    if (notification && !notification.read && notification.toast !== false
        && TOASTED_TYPES.has(notification.type)) {
      addToastToQueue(notification)
    }
  }
}, { immediate: true })

const clientPackJob = useClientPackJob()
const handleToastAction = (notification) => {
  const action = notification.action
  if (!action) return
  markAsRead(notification.id)
  if (action.kind === 'client_pack_download') clientPackJob.triggerDownload(action.sessionId)
  else if (action.kind === 'client_pack_retry') clientPackJob.retry(action.sessionId)
}

</script>

<style scoped>
/* Sidebar scrollbar */
.sidebar-scroll {
  /* Prevent scroll animation */
  scroll-behavior: auto !important;
}
.sidebar-scroll::-webkit-scrollbar {
  width: 4px;
}

.sidebar-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.15);
  border-radius: var(--nviya-radius-sm);
}

.sidebar-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.25);
}

/* moved to assets/css/main.css — `:global()` in a <style scoped> block
   compiles to a bare `.dark` rule in this project and leaks onto <html> */

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Dropdown transition */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
